import { useState, useEffect, useRef } from 'react';
import { socket } from "../socket";
import PhaserCanvas from "../phaser/PhaserCanvas";
import StartMenu from '../components/StartMenu';
import TestUI from '../components/TestUI';
import DebugLog from '../components/DebugLog';
import CardDisplay from '../components/CardDisplay';
import '../stylesheets/style.css';
import JoinGameMenu from '../components/JoinGameMenu';
import CreateGameMenu from '../components/CreateGameMenu';
import GameOver from '../components/GameOver';
import InstructionsModal from '../components/InstructionsModal';
import Header from '../components/Header';
import EmojiPanel from "../components/EmojiPanel";
import FeedbackToast from "../components/FeedbackToast";

import {
  trackGameCreated,
  trackPlayerJoined,
  trackPlayerDisconnected,
  trackGameEnded,
  trackPromptChosen,  
} from "../analytics";

export default function GamePage() {
    const [serverState, setServerState] = useState(null);
    const stateRef = useRef(null);
    const [game, setGame] = useState(null);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [rollToast, setRollToast] = useState(null);
    const [gameOver, setGameOver] = useState(null);
    const [showInstructions, setShowInstructions] = useState(false);
    const [socketError, setSocketError] = useState(null);

    // Draggable CardDisplay state
    const [cardPosition, setCardPosition] = useState({ x: 20, y: 20 });
    const [isDraggingCard, setIsDraggingCard] = useState(false);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const gameContainerRef = useRef(null);
    const cardRef = useRef(null);
    const cardSizeRef = useRef({ width: 0, height: 0 });

    // State to manage which menu is visible
    const [showJoin, setShowJoin] = useState(false);
    const [showCreateGameMenu, setShowCreateGameMenu] = useState(false);

    // Shows the create game menu form
    const handleStartGame = () => {
        setShowCreateGameMenu(true);
    };

    // function that executes when the user submits the create game form
    const handleCreateGame = (gameData) => {
        // First we emit the create game event to the server
        socket.emit("createGame", {
            name: gameData.name,
            settings: {
                numSpaces: gameData.numberOfSquares,
            }
        });
        // After that we hide the create game menu
        setShowCreateGameMenu(false);
    };

    // Function to cancel the creation of the game when the user clicks the cancel button, we just hide the create game menu
    const handleCancelCreateGame = () => {
        setShowCreateGameMenu(false);
    };

    useEffect(() => {
        // when you create a game: save the state, switch to board
        const onGameCreated = ({ state }) => {
            if (game && !isGameStarted) {
                setServerState(state);
                stateRef.current = state;
                game.scene.start("BoardScene", { gameState: state });
                setIsGameStarted(true);

                trackGameCreated(state.gameId ?? state.id);
            }
        };

        // when a new player (yourself) joins: update state, switch to board
        const onPlayerJoined = ({ state }) => {
            if (game && !isGameStarted) {
                setServerState(state);
                stateRef.current = state;
                game.scene.start("BoardScene", { gameState: state });
                setIsGameStarted(true);

                trackPlayerJoined(state.gameId ?? state.id);
            }
        };

        // Recieve stateUpdate: save to client
        const onStateUpdate = ({ state }) => {
            setServerState(state);
            stateRef.current = state;
        };

        // display Toast with the outcome of the Roll
        const onDiceRolled = ({ playerId, roll }) => {
            const name = stateRef.current?.players?.[playerId]?.name || playerId;
            setRollToast({ name, roll });
            setTimeout(() => setRollToast(null), 5000);
        };

        // handler for card drawn event
        const onCardDrawn = ({ card, playerId }) => {
            const name = stateRef.current?.players?.[playerId]?.name || playerId;
            console.log(`${name} drew card:`, card.prompt);

            if (playerId === socket.id) {
                trackPromptChosen(card.deck, card.id);
                console.log(card.deck, card.id);
            }
        };

        // Listen for gameOver event from server
        const onGameOver = ({ winner }) => {
            setGameOver({ winner });
            const gameId = stateRef.current?.gameId || stateRef.current?.id;
            const playerCount = Object.keys(stateRef.current?.players || {}).length;
            const turnCount = stateRef.current?.turnCount || 0;

            if (gameId) {
                trackGameEnded(gameId, turnCount, playerCount);
            }          
        };

        // socket event listeners
        socket.on('stateUpdate', onStateUpdate);
        socket.on("gameCreated", onGameCreated);
        socket.on("playerJoined", onPlayerJoined);
        socket.on("diceRolled", onDiceRolled);
        socket.on("cardDrawn", onCardDrawn);
        socket.on('gameOver', onGameOver);

        // destroy sockets on disconnect
        return () => {
            socket.off("gameCreated", onGameCreated);
            socket.off("playerJoined", onPlayerJoined);
            socket.off("diceRolled", onDiceRolled);
            socket.off('stateUpdate', onStateUpdate);
            socket.off("cardDrawn", onCardDrawn);
            socket.off('gameOver', onGameOver);
        };
    }, [game, isGameStarted]);

    // Handler to restart game (go back to start screen)
    const handleRestart = () => {
        setGameOver(null);
        setIsGameStarted(false);
        setServerState(null);
        setShowCreateGameMenu(false);
        setShowJoin(false);
        // Optionally reset other state here
        // The StartMenu will now be shown
    };

    // Handle starting drag on the card
    const handleCardMouseDown = (event) => {
        if (!gameContainerRef.current) return;

        const containerRect = gameContainerRef.current.getBoundingClientRect();
        const pointerX = event.clientX - containerRect.left;
        const pointerY = event.clientY - containerRect.top;

        if (cardRef.current) {
            const cardRect = cardRef.current.getBoundingClientRect();
            cardSizeRef.current = {
                width: cardRect.width,
                height: cardRect.height,
            };
        }

        dragOffsetRef.current = {
            x: pointerX - cardPosition.x,
            y: pointerY - cardPosition.y,
        };

        setIsDraggingCard(true);
    };

    // Global mouse move / up listeners while dragging
    useEffect(() => {
        if (!isDraggingCard) return;

        const handleMouseMove = (event) => {
            if (!gameContainerRef.current) return;

            const containerRect = gameContainerRef.current.getBoundingClientRect();
            const pointerX = event.clientX - containerRect.left;
            const pointerY = event.clientY - containerRect.top;

            const { width, height } = cardSizeRef.current;

            // Raw (unclamped) position
            let newX = pointerX - dragOffsetRef.current.x;
            let newY = pointerY - dragOffsetRef.current.y;

            // Clamp so the whole card stays inside the container
            const minX = 0;
            const minY = 0;
            const maxX = containerRect.width - width;
            const maxY = containerRect.height - height;

            newX = Math.min(Math.max(newX, minX), maxX);
            newY = Math.min(Math.max(newY, minY), maxY);

            setCardPosition({ x: newX, y: newY });
        };

        const handleMouseUp = () => {
            setIsDraggingCard(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingCard]);

    useEffect(() => {
        const handleLeave = () => {
            const game = stateRef.current;
            const gameId = game?.gameId || game?.id;
            if (!game || !gameId) return;

            trackPlayerDisconnected(gameId);
            
            const playerCount = Object.keys(game.players || {}).length;
            const turnCount = game.turnCount || 0;

            if (playerCount === 1) {
            trackGameEnded(gameId, turnCount, playerCount);
            }
        };

        window.addEventListener("beforeunload", handleLeave);
        return () => window.removeEventListener("beforeunload", handleLeave);
    }, []);

    useEffect(() => {
        const handleConnect = () => {
            setSocketError(null);
        };

        const handleConnectError = (error) => {
            console.error("Socket connection error:", error);

            setSocketError(
                "Unable to connect to the game server. Your network may be blocking the game connection. Try another Wi-Fi network or mobile data."
            );
        };

        const handleDisconnect = (reason) => {
            console.warn("Socket disconnected:", reason);

            if (reason !== "io client disconnect") {
                setSocketError(
                    "Connection to the game server was lost. Check your internet connection or try another network."
                );
            }
        };

        socket.on("connect", handleConnect);
        socket.on("connect_error", handleConnectError);
        socket.on("disconnect", handleDisconnect);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("connect_error", handleConnectError);
            socket.off("disconnect", handleDisconnect);
        };
    }, []);

    return (
        <div className='page-container'>
            <Header/>

            {socketError && (
                <div className="socket-error">
                    <strong>Connection Problem</strong>
                    <p>{socketError}</p>

                    <button type="button" onClick={() => window.location.reload()}>
                        Try Again
                    </button>
                </div>
            )}

            <FeedbackToast gameState={serverState} />

            {rollToast && (
                <div className="dice-toast">
                    <div className="dice-toast-card">
                        <div className="dice-toast-title">{rollToast.name} rolled</div>
                        <div className="dice-toast-roll">{rollToast.roll}</div>
                    </div>
                </div>
            )}

            {gameOver && (
                <GameOver winner={gameOver.winner} onRestart={handleRestart} />
            )}

            <div className='game-ui-wrapper'>
                <div className='game-container' ref={gameContainerRef}>
                    <PhaserCanvas onGameReady={setGame} />

{/*                     {isGameStarted && (
                        <button
                            type="button"
                            className="start-menu-button instructions-toggle-button"
                            onClick={() => setShowInstructions(true)}
                        >
                            Instructions
                        </button>
                    )} */}

                    {/* In-game UI */}
                    {isGameStarted && <DebugLog gameState={serverState} />}
                    {isGameStarted && serverState?.phase === "awaitingEndTurn" && serverState?.activeCard && (
                        <div
                            ref={cardRef}
                            onMouseDown={handleCardMouseDown}
                            style={{
                                position: 'absolute',
                                left: cardPosition.x,
                                top: cardPosition.y,
                                cursor: isDraggingCard ? 'grabbing' : 'grab',
                            }}
                        >
                            <CardDisplay card={serverState.activeCard} />
                        </div>
                    )}

                    {/* Pre-game Menus */}
                    {!isGameStarted && (
                        <>
                            {showCreateGameMenu ? (<CreateGameMenu onCreateGame={handleCreateGame} onCancel={handleCancelCreateGame}/>) 
                            : showJoin ? (<JoinGameMenu onBackClick={() => setShowJoin(false)} />) 
                            : (<StartMenu onStartGame={handleStartGame} onJoinClick={() => setShowJoin(true)} />)}
                        </>
                    )}
                </div>

                <InstructionsModal
                    isOpen={showInstructions}
                    onClose={() => setShowInstructions(false)}
                />

                {isGameStarted && serverState && <TestUI gameState={serverState} />}

                {isGameStarted && serverState && (
                    <EmojiPanel gameState={serverState} />
                )}
            </div>
        </div>
    );
}